// Sistem Yönetimi — şirketler (tenant) ve tesisleri.
//
// Sol tabloda tüm tenant'lar, altında tesisleri; sağda seçilen şirketin detayı
// ve yaşam döngüsü eylemleri. Yerleşim SCADA etiket ekranıyla aynı desende
// (%40 / %60) — süper admin de aynı ürünün içinde.
//
// SİLME YOK: tenant ve tesis silinmiyor, askıya alınıyor. Silme altındaki tüm
// işletme verisini (onaylı beyanlar, K15/K39 gereği değiştirilemez stok
// hareketleri) düşürürdü. Askı geri alınabilir.

import { Fragment, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trTarihSaat } from '../../lib/trFormat'
import SistemKabuk from './SistemKabuk'
import TenantDrawer, { TesisDrawer } from './TenantDrawer'
import { sistem, type TenantDurumu, type TenantSatiri } from './api'

const YERLESIM = { gridTemplateColumns: 'minmax(320px, 40%) minmax(0, 1fr)' }

const TABLO: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  maxHeight: 'calc(100vh - 118px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const DURUM: Record<TenantDurumu, { etiket: string; sinif: string; not: string }> = {
  aktif: { etiket: 'Aktif', sinif: 'b-navy', not: 'Kullanıcılar giriş yapabiliyor' },
  askida: {
    etiket: 'Askıda',
    sinif: 'b-amber',
    not: 'Giriş kapalı — veri korunuyor, askı geri alınabilir',
  },
  pasif: { etiket: 'Pasif', sinif: 'b-gray', not: 'Giriş kapalı — kullanım dışı' },
}

function DurumRozeti({ durum }: { durum: TenantDurumu }) {
  const d = DURUM[durum]
  return (
    <span className={`badge ${d.sinif}`} title={d.not}>
      {durum === 'aktif' && <span className="dot" />}
      {d.etiket}
    </span>
  )
}

function Icerik() {
  const kuyruk = useQueryClient()
  const { ac } = useDrawer()
  const [secim, setSecim] = useState<string | null>(null)
  const [arama, setArama] = useState('')
  const [segment, setSegment] = useState<'tumu' | TenantDurumu>('tumu')

  const { data: tenantlar = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sistem-tenantlar'],
    queryFn: sistem.tenantlar,
  })

  const tazele = () => kuyruk.invalidateQueries({ queryKey: ['sistem-tenantlar'] })

  const liste = useMemo(
    () =>
      tenantlar.filter((t) => {
        if (segment !== 'tumu' && t.durum !== segment) return false
        if (!arama.trim()) return true
        const a = arama.trim().toLocaleLowerCase('tr')
        return [t.unvan, t.kod ?? '', t.vergiNo ?? '', ...t.tesisler.map((x) => `${x.kod} ${x.ad}`)]
          .join(' ')
          .toLocaleLowerCase('tr')
          .includes(a)
      }),
    [tenantlar, segment, arama],
  )

  const ilkId = liste[0]?.id
  useEffect(() => {
    setSecim((s) => (s && liste.some((t) => t.id === s) ? s : (ilkId ?? null)))
  }, [ilkId, liste])

  const secili = tenantlar.find((t) => t.id === secim) ?? null

  const durumDegis = useMutation({
    mutationFn: (g: { id: string; hedef: TenantDurumu; gerekce: string }) =>
      g.hedef === 'aktif'
        ? sistem.etkinlestir(g.id)
        : g.hedef === 'askida'
          ? sistem.askiyaAl(g.id, g.gerekce)
          : sistem.pasifeAl(g.id, g.gerekce),
    onSuccess: (t) => {
      tazele()
      toast(
        t.durum === 'aktif'
          ? `${t.unvan} · erişim açıldı`
          : `${t.unvan} · erişim kapatıldı — açık oturumlar da sonlandırıldı`,
      )
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Durum değiştirilemedi'),
  })

  const senkronize = useMutation({
    mutationFn: (id: string) => sistem.senkronize(id),
    onSuccess: (k) => {
      tazele()
      toast(
        k.uniteSinifi + k.stokKarti + k.esik === 0
          ? `Kütüphane zaten güncel — ${tr(k.atlanan, 0)} kalem atlandı`
          : `Kütüphane tamamlandı: +${tr(k.uniteSinifi, 0)} sınıf, +${tr(k.stokKarti, 0)} kart, +${tr(k.esik, 0)} eşik`,
      )
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Senkronizasyon başarısız'),
  })

  /** Askı/pasif gerekçesini soran küçük akış — gerekçe sunucuda zorunlu. */
  function erisimKapat(t: TenantSatiri, hedef: 'askida' | 'pasif') {
    const gerekce = window.prompt(
      hedef === 'askida'
        ? `${t.unvan} askıya alınacak. Gerekçe (denetim satırına yazılır):`
        : `${t.unvan} pasife alınacak. Gerekçe (denetim satırına yazılır):`,
      '',
    )
    if (gerekce === null) return
    if (!gerekce.trim()) {
      toast('Gerekçe zorunlu — erişimi kapatmanın nedeni kayda geçmeli')
      return
    }
    durumDegis.mutate({ id: t.id, hedef, gerekce: gerekce.trim() })
  }

  const toplam = useMemo(
    () => ({
      tenant: tenantlar.length,
      aktif: tenantlar.filter((t) => t.durum === 'aktif').length,
      askida: tenantlar.filter((t) => t.durum !== 'aktif').length,
      tesis: tenantlar.reduce((s, t) => s + t.tesisSayisi, 0),
      aktifTesis: tenantlar.reduce((s, t) => s + t.aktifTesisSayisi, 0),
      kullanici: tenantlar.reduce((s, t) => s + t.kullaniciSayisi, 0),
      aktifKullanici: tenantlar.reduce((s, t) => s + t.aktifKullaniciSayisi, 0),
    }),
    [tenantlar],
  )

  const sayac = (a: 'tumu' | TenantDurumu) =>
    a === 'tumu' ? tenantlar.length : tenantlar.filter((t) => t.durum === a).length

  return (
    <>
      <div className="crumb">
        Sistem Yönetimi <b>/ Şirketler</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Şirketler ve Tesisler</h1>
        <div className="page-actions">
          <button
            className="btn btn-pri"
            type="button"
            onClick={() =>
              ac({
                baslik: 'Yeni şirket',
                altBaslik: 'Şirket + ilk tesis + tesis sorumlusu',
                boyut: 'md',
                icerik: <TenantDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Şirket ekle
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Şirket</div>
          {/* Hata hâlinde 0 yazmak "hiç şirket yok" demektir; "—" bilinmiyor demek. */}
          <div className="k-v">{isError ? '—' : tr(toplam.tenant, 0)}</div>
          <div className="k-s">{tr(toplam.aktif, 0)} aktif · {tr(toplam.askida, 0)} kapalı</div>
        </div>
        <div className="k">
          <div className="k-l">Tesis</div>
          <div className="k-v">{isError ? '—' : tr(toplam.tesis, 0)}</div>
          <div className="k-s">{tr(toplam.aktifTesis, 0)} aktif</div>
        </div>
        <div className="k">
          <div className="k-l">Kullanıcı</div>
          <div className="k-v">{isError ? '—' : tr(toplam.kullanici, 0)}</div>
          <div className="k-s">{tr(toplam.aktifKullanici, 0)} aktif hesap</div>
        </div>
        <div className="k">
          <div className="k-l">Erişimi Kapalı</div>
          <div className="k-v">{isError ? '—' : tr(toplam.askida, 0)}</div>
          <div className="k-s">askıda ya da pasif</div>
        </div>
        <div className="k">
          <div className="k-l">Ortalama Tesis</div>
          <div className="k-v">
            {isError || toplam.tenant === 0 ? '—' : tr(toplam.tesis / toplam.tenant, 1)}
          </div>
          <div className="k-s">şirket başına</div>
        </div>
      </div>

      <div className="list-grid" style={YERLESIM}>
        <div className="card" style={TABLO}>
          <div className="toolbar">
            <div className="seg">
              {(['tumu', 'aktif', 'askida', 'pasif'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  className={segment === a ? 'seg-i on' : 'seg-i'}
                  onClick={() => setSegment(a)}
                >
                  {a === 'tumu' ? 'Tümü' : DURUM[a].etiket}
                  <small>{sayac(a)}</small>
                </button>
              ))}
            </div>
            <div className="f-search" style={{ width: '100%' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Unvan, kod, VKN, tesis ara…"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table className="grid" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Şirket / Tesis</th>
                  <th style={{ width: 78 }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={2} className="cell-sub" style={{ padding: 14 }}>
                      Yükleniyor…
                    </td>
                  </tr>
                )}
                {/*
                  HATA ile BOŞ ayrı: liste yüklenemediğinde "henüz şirket yok"
                  yazmak yalan — kullanıcı sistemin boş olduğunu sanıp yeni
                  şirket açmaya kalkardı. Sunucu erişilemezken tek doğru mesaj
                  erişilemediğidir.
                */}
                {isError && (
                  <tr>
                    <td colSpan={2} style={{ padding: 14 }}>
                      <div className="cell-name" style={{ color: 'var(--red)' }}>
                        Liste yüklenemedi
                      </div>
                      <div className="cell-sub" style={{ marginBottom: 6 }}>
                        {error instanceof ApiHatasi
                          ? error.message
                          : 'Sunucuya ulaşılamadı.'}
                      </div>
                      <button className="btn btn-sec btn-sm" type="button" onClick={() => void refetch()}>
                        Yeniden dene
                      </button>
                    </td>
                  </tr>
                )}
                {!isLoading && !isError && liste.length === 0 && (
                  <tr>
                    <td colSpan={2} className="cell-sub" style={{ padding: 14 }}>
                      {tenantlar.length === 0
                        ? 'Henüz şirket yok — "Şirket ekle" ile başlayın.'
                        : 'Süzgece uyan şirket yok.'}
                    </td>
                  </tr>
                )}

                {liste.map((t) => (
                  <Fragment key={t.id}>
                    <tr
                      className={secim === t.id ? 'sel' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSecim(t.id)}
                    >
                      <td>
                        <div className="cell-name">{t.unvan}</div>
                        <div className="cell-sub" style={{ overflowWrap: 'anywhere' }}>
                          {t.kod && <span className="kod">{t.kod}</span>}
                          {t.kod ? ' · ' : ''}
                          {tr(t.tesisSayisi, 0)} tesis · {tr(t.kullaniciSayisi, 0)} kullanıcı
                        </div>
                      </td>
                      <td>
                        <DurumRozeti durum={t.durum} />
                      </td>
                    </tr>

                    {secim === t.id &&
                      t.tesisler.map((x) => (
                        <tr key={x.id} onClick={() => setSecim(t.id)}>
                          <td style={{ paddingLeft: 20 }}>
                            <div className="cell-name">{x.ad}</div>
                            <div className="cell-sub">
                              <span className="kod">{x.kod}</span>
                              {x.il ? ` · ${x.il}` : ''} · {tr(x.kullaniciSayisi, 0)} kullanıcı
                            </div>
                          </td>
                          <td>
                            {x.aktif ? (
                              <span className="badge b-navy">Aktif</span>
                            ) : (
                              <span className="badge b-gray">Kapalı</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid-foot" style={{ borderTop: '1px solid var(--line-soft)' }}>
            <span>{tr(liste.length, 0)} şirket · silme yok, erişim askıya alınır</span>
          </div>
        </div>

        {secili ? (
          <Detay
            tenant={secili}
            erisimKapat={erisimKapat}
            etkinlestir={(t) => durumDegis.mutate({ id: t.id, hedef: 'aktif', gerekce: '' })}
            senkronize={(t) => senkronize.mutate(t.id)}
            calisiyor={durumDegis.isPending || senkronize.isPending}
          />
        ) : (
          <div className="card">
            <div className="insp-empty">Soldan bir şirket seçin.</div>
          </div>
        )}
      </div>
    </>
  )
}

function Detay({
  tenant: t,
  erisimKapat,
  etkinlestir,
  senkronize,
  calisiyor,
}: {
  tenant: TenantSatiri
  erisimKapat: (t: TenantSatiri, hedef: 'askida' | 'pasif') => void
  etkinlestir: (t: TenantSatiri) => void
  senkronize: (t: TenantSatiri) => void
  calisiyor: boolean
}) {
  const { ac } = useDrawer()
  const d = DURUM[t.durum]

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div className="insp-h" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="insp-eyebrow">
            {t.kod && <span className="kod">{t.kod}</span>}
            {t.kod && <span>·</span>}
            <span>Şirket</span>
            {t.vergiNo && (
              <>
                <span>·</span>
                <span className="mono">VKN {t.vergiNo}</span>
              </>
            )}
          </div>
          <div className="insp-t">
            {t.unvan} <DurumRozeti durum={t.durum} />
          </div>
          <div className="cell-sub" style={{ marginTop: 2 }}>
            {d.not}
            {t.durumZamani ? ` · ${trTarihSaat(t.durumZamani)}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            onClick={() =>
              ac({
                baslik: 'Şirketi düzenle',
                altBaslik: t.unvan,
                boyut: 'md',
                icerik: <TenantDrawer mevcut={t} />,
              })
            }
          >
            Düzenle
          </button>
          {t.durum === 'aktif' ? (
            <button
              className="btn btn-danger btn-sm"
              type="button"
              disabled={calisiyor}
              title="Girişi kapatır; veri korunur, geri alınabilir"
              onClick={() => erisimKapat(t, 'askida')}
            >
              Askıya al
            </button>
          ) : (
            <button
              className="btn btn-pri btn-sm"
              type="button"
              disabled={calisiyor}
              onClick={() => etkinlestir(t)}
            >
              Erişimi aç
            </button>
          )}
        </div>
      </div>

      {t.durum !== 'aktif' && t.durumGerekce && (
        <div className="info-box" style={{ margin: 12 }}>
          <b>Erişim kapalı.</b>
          <span>
            Gerekçe: {t.durumGerekce}. Bu metin kullanıcıya GÖSTERİLMİYOR — giriş
            ekranında yalnız "yöneticinizle görüşün" yazıyor.
          </span>
        </div>
      )}

      <div className="sect-t">Özet</div>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 180 }}>Gösterge</th>
            <th style={{ width: 200 }}>Değer</th>
            <th>Açıklama</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Tesis</td>
            <td className="mono" style={{ fontWeight: 600 }}>
              {tr(t.aktifTesisSayisi, 0)} / {tr(t.tesisSayisi, 0)}
            </td>
            <td className="cell-sub">aktif / toplam</td>
          </tr>
          <tr>
            <td>Kullanıcı</td>
            <td className="mono" style={{ fontWeight: 600 }}>
              {tr(t.aktifKullaniciSayisi, 0)} / {tr(t.kullaniciSayisi, 0)}
            </td>
            <td className="cell-sub">aktif / toplam</td>
          </tr>
          <tr>
            <td>Son giriş</td>
            <td className="mono" style={{ fontWeight: 600 }}>
              {t.sonGiris ? trTarihSaat(t.sonGiris) : '—'}
            </td>
            <td className="cell-sub">şirketteki herhangi bir kullanıcı</td>
          </tr>
          <tr>
            <td>Kuruluş</td>
            <td className="mono" style={{ fontWeight: 600 }}>
              {trTarihSaat(t.olusturmaZamani)}
            </td>
            <td className="cell-sub">sistemdeki kayıt tarihi</td>
          </tr>
        </tbody>
      </table>

      <div className="toolbar">
        <div className="toolbar-right" style={{ marginRight: 'auto' }}>
          <span>{tr(t.tesisler.length, 0)} tesis</span>
        </div>
        <button
          className="btn btn-sec btn-sm"
          type="button"
          disabled={calisiyor}
          title="Kütüphanede olup tesiste olmayan kalemleri EKLER; mevcutların üzerine yazmaz"
          onClick={() => senkronize(t)}
        >
          Kütüphaneyi tamamla
        </button>
        <button
          className="btn btn-pri btn-sm"
          type="button"
          onClick={() =>
            ac({
              baslik: 'Yeni tesis',
              altBaslik: t.unvan,
              boyut: 'md',
              icerik: <TesisDrawer tenantId={t.id} />,
            })
          }
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Tesis ekle
        </button>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 110 }}>Kod</th>
            <th>Ad</th>
            <th style={{ width: 130 }}>İl</th>
            <th className="num" style={{ width: 90 }}>Kullanıcı</th>
            <th style={{ width: 80 }}>Durum</th>
            <th style={{ width: 90 }} />
          </tr>
        </thead>
        <tbody>
          {t.tesisler.length === 0 && (
            <tr>
              <td colSpan={6} className="cell-sub" style={{ padding: 14 }}>
                Tesis yok — şirkete kimse giriş yapamaz.
              </td>
            </tr>
          )}
          {t.tesisler.map((x) => (
            <tr key={x.id}>
              <td>
                <span className="kod">{x.kod}</span>
              </td>
              <td>
                <div className="cell-name">{x.ad}</div>
              </td>
              <td className="cell-sub">{x.il ?? '—'}</td>
              <td className="num">{tr(x.kullaniciSayisi, 0)}</td>
              <td>
                {x.aktif ? (
                  <span className="badge b-navy">Aktif</span>
                ) : (
                  <span className="badge b-gray">Kapalı</span>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() =>
                      ac({
                        baslik: 'Tesisi düzenle',
                        altBaslik: `${t.unvan} · ${x.kod}`,
                        boyut: 'md',
                        icerik: <TesisDrawer tenantId={t.id} mevcut={x} />,
                      })
                    }
                  >
                    Düzenle
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rule">
        <b>Not</b>
        <span>
          Tesis kapatmak o tesise bağlı açık oturumları da sonlandırır. Şirket
          askıya alındığında tüm oturumlar kapanır ve giriş 403 döner.
        </span>
      </div>
    </div>
  )
}

export default function SirketlerSayfasi() {
  return (
    <SistemKabuk>
      <Icerik />
    </SistemKabuk>
  )
}
